import {noop} from '@benchling/js-utils';
import React, {useRef} from 'react';
import {v4 as uuidv4} from 'uuid';

import {
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {MethodUpdater} from 'procedures/design/ProcedureEditor';
import {
  isInaccessibleProcedureTestDefinition,
  Method,
  ProcedureTestDefinition,
} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import TestDefinitionsTableAdapter, {
  TestDefinitionsTableMetadata,
  TestDefinitionsTableRowMetadata,
} from 'procedures/tables/TestDefinitionsTableAdapter';
import useOnMount from 'ui-library/hooks/useOnMount';

import {ProcedureValidationContext} from '../validation/ProcedureValidationContext';
import ProcedureConfigTable from './ProcedureConfigTable';

const emptyCreateTestDefinitionsTableProps: CreateStructuredTableProps<TestDefinitionsTableMetadata> = {
  name: '',
  schemas: [],
  entryId: null,
  selectorsById: {},
  structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  metadata: {},
};

interface TestDefinitionsTableProps {
  method: Method;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  onReadyForEditing: () => void;
  locked: boolean;
}

export default function TestDefinitionsTable(props: TestDefinitionsTableProps): JSX.Element {
  const {method, onChangeMethod, onReadyForEditing} = props;
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const {procedureTemplateCollectionOwner} = ProcedurePageContext.useContext();
  const adapter = TestDefinitionsTableAdapter.getAdapter();
  const lsuvs = useRef<Record<string, Array<LastSyncedUpstreamValue>> | null>(null);
  const baseCreateTestDefinitionsTableProps: CreateStructuredTableProps<TestDefinitionsTableMetadata> = {
    ...emptyCreateTestDefinitionsTableProps,
    metadata: {procedureTemplateCollectionOwner},
  };

  const onTestDefinitionsChangeDetails = (testDefinitions: Array<ProcedureTestDefinition>): void => {
    onChangeMethod(method.cid, (m) => ({...m, testDefinitions}));
    lsuvs.current = TestDefinitionsTableAdapter.computeLSUVs(table.rows, table.columns, testDefinitions);
  };

  useOnMount(onReadyForEditing);

  const testDefinitions =
    method.testDefinitions.length > 0
      ? (method.testDefinitions.filter(
          (td) => !isInaccessibleProcedureTestDefinition(td)
        ) as Array<ProcedureTestDefinition>)
      : [TestDefinitionsTableAdapter.makeEmptyTestDefinition()];

  const tableCid = `testDefinitionsTable-${uuidv4()}`;

  let table = adapter.createTableFromTestDefinitions(
    baseCreateTestDefinitionsTableProps,
    tableCid,
    testDefinitions,
    procedureValidationErrors
  );

  if (lsuvs.current == null) {
    lsuvs.current = TestDefinitionsTableAdapter.computeLSUVs(table.rows, table.columns, testDefinitions);
  }

  table = {
    ...table,
    rows: table.rows.map((row, index) => {
      const id = testDefinitions[index].id;
      return {
        ...row,
        ...(id != null && lsuvs.current![id] ? {lastSyncedUpstreamValues: lsuvs.current![id]} : null),
        // A row must be finalized before submission, which has UI implications, such as disabling editing.
        isFinalized: id != null,
      };
    }),
  };

  return (
    <ProcedureConfigTable
      tableAdapter={adapter}
      createStructuredTableProps={baseCreateTestDefinitionsTableProps}
      onChangeDetails={onTestDefinitionsChangeDetails}
      onChangeErrorState={noop}
      rowToDetail={(
        row: StructuredTableRow<TestDefinitionsTableRowMetadata>,
        columns: Array<StructuredTableColumn>
      ) => TestDefinitionsTableAdapter.rowToTestDefinition(columns, row)}
      table={table}
      tableCid={tableCid}
      locked={props.locked}
      dataTestAttrs={{component: 'TestDefinitionsTable', element: 'root'}}
    />
  );
}
